import { useCallback, useEffect, useRef, useState } from "react";
import { getUnifiedSuggestions, UnifiedSuggestItem } from "../../apis/search";
import { generateSessionToken } from "./sessionToken";

export type SelectedVenue = {
  name: string;
  subtitle: string;
  /** Null for free text. */
  placeId: string | null;
  /** Already on Sociability — offer a claim. */
  inDb: boolean;
  businessId: number | string | null;
  slug: string | null;
};

const DEBOUNCE_MS = 300;
// Shorter prefixes are mostly noise, and each is a billable Google request.
const MIN_TERM_LENGTH = 3;

type Options = {
  /** Ranking bias only; results stay global. */
  latitude?: number;
  longitude?: number;
  enabled?: boolean;
};

/**
 * Business-name autosuggest (venues scope): our businesses plus Google
 * establishments. Selection doesn't resolve coordinates — pass the place_id to
 * place-from-google (lookup mode) to validate and prefill.
 */
export function useVenueSuggest(term: string, options: Options = {}) {
  const { latitude, longitude, enabled = true } = options;

  const [items, setItems] = useState<UnifiedSuggestItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const sessionTokenRef = useRef<string>("");
  const requestIdRef = useRef(0);

  const ensureSessionToken = useCallback(() => {
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = generateSessionToken();
    }
    return sessionTokenRef.current;
  }, []);

  const resetSessionToken = useCallback(() => {
    sessionTokenRef.current = "";
  }, []);

  const trimmed = term.trim();

  useEffect(() => {
    if (!enabled || trimmed.length < MIN_TERM_LENGTH) {
      requestIdRef.current += 1;
      setItems([]);
      setIsLoading(false);
      return;
    }

    const timer = setTimeout(async () => {
      const requestId = ++requestIdRef.current;
      setIsLoading(true);
      try {
        const list = await getUnifiedSuggestions(trimmed, {
          latitude,
          longitude,
          sessionToken: ensureSessionToken(),
          scope: "venues",
        });
        if (requestId !== requestIdRef.current) return;
        setItems(list);
      } catch {
        if (requestId !== requestIdRef.current) return;
        setItems([]);
      } finally {
        if (requestId === requestIdRef.current) setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [trimmed, latitude, longitude, enabled, ensureSessionToken]);

  // No token reset: the session stays open until the caller resolves the place_id.
  const selectItem = useCallback(
    (item: UnifiedSuggestItem): SelectedVenue => ({
      name: item.displayText,
      subtitle: item.subtitle,
      placeId: item.data?.place_id ?? null,
      inDb: Boolean(item.data?.inDb),
      businessId: item.data?.businessId ?? null,
      slug: item.data?.slug ?? null,
    }),
    [],
  );

  /** User kept what they typed; no place to resolve. */
  const selectFreeText = useCallback(
    (text: string): SelectedVenue | null => {
      const name = text.trim();
      if (!name) return null;

      resetSessionToken();
      return {
        name,
        subtitle: "",
        placeId: null,
        inDb: false,
        businessId: null,
        slug: null,
      };
    },
    [resetSessionToken],
  );

  /** Pass to the lookup call so the session bills as one. */
  const getSessionToken = useCallback(() => sessionTokenRef.current || null, []);

  return {
    items,
    isLoading,
    selectItem,
    selectFreeText,
    getSessionToken,
    resetSessionToken,
  };
}
